import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { useAvisos } from '#components/mobile/avisos/useAvisos';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { useTrato } from '#components/mobile/travesia/trato';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { BotonRedondo } from '#components/mobile/ui/Cabecera';
import { Cargando } from '#components/mobile/ui/Cargando';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Tarjeta } from '#components/mobile/ui/Tarjeta';
import {
  color,
  espacio,
  radio,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useGlobalPref } from '#hooks/useGlobalPref';
import { useNavigate } from '#hooks/useNavigate';
import { pedirRumbo } from '#uso';

import { preguntarAGavi } from './conversacion';
import type { Mensaje, RespuestaTurno } from './conversacion';
import { ejecutarHerramienta, RUTAS_PANTALLA } from './herramientas';
import type { Pantalla } from './herramientas';
import { useGaviInteligente } from './useGaviInteligente';

type Gasto = RespuestaTurno['gasto'];

type Burbuja = {
  id: number;
  rol: 'user' | 'assistant';
  texto: string;
  herramientas: string[];
  pantalla?: Pantalla;
};

const LARGO_PREGUNTA = 2000;
/** Aceptó la hoja de la primera vez en este móvil. */
const CLAVE_PERMISO = 'rumbo-gavi-ia-aceptado';

function leerPermiso(): boolean {
  try {
    return window.localStorage.getItem(CLAVE_PERMISO) === '1';
  } catch {
    return false;
  }
}

function guardarPermiso() {
  try {
    window.localStorage.setItem(CLAVE_PERMISO, '1');
  } catch {
    // Sin almacenamiento: se volverá a preguntar la próxima vez.
  }
}

const estiloCampo: CSSProperties = {
  width: '100%',
  minHeight: TACTIL + 4,
  borderRadius: radio.boton,
  backgroundColor: color.surface2,
  border: `1px solid ${color.line}`,
  padding: '12px 14px',
  fontSize: 16,
  lineHeight: 1.4,
  color: color.fg,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  outline: 'none',
  resize: 'vertical',
};

/**
 * Gavi inteligente (Vadik, 31-oct-2026): Gavi contesta con Claude a lo que
 * preguntes de tu dinero. Solo para quien administra Rumbo y solo en un
 * presupuesto que es solo suyo. Las cuentas las hace este móvil sobre el
 * presupuesto descifrado; el servidor solo pone la clave de la API. Gavi solo
 * mira: nunca cambia nada. La conversación vive en esta pantalla y se pierde
 * al salir.
 */
export function GaviPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const disponible = useGaviInteligente();
  const [nombre] = useGlobalPref('nombrePerfil');
  const { contexto } = useTrato();
  const { avisos } = useAvisos();

  const [estado, setEstado] = useState<{
    hayClave: boolean;
    gasto: Gasto;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aceptado, setAceptado] = useState(leerPermiso);
  const [burbujas, setBurbujas] = useState<Burbuja[]>([]);
  const [historial, setHistorial] = useState<Mensaje[]>([]);
  const [pregunta, setPregunta] = useState('');
  const [pensando, setPensando] = useState(false);
  const siguienteId = useRef(1);
  const final = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!disponible) {
      return;
    }
    let vivo = true;
    pedirRumbo<{ hayClave: boolean; gasto: Gasto }>('/rumbo/gavi')
      .then(e => vivo && setEstado(e))
      .catch((e: Error) => vivo && setError(e.message));
    return () => {
      vivo = false;
    };
  }, [disponible]);

  useEffect(() => {
    final.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [burbujas.length, pensando]);

  const ficha = {
    hoy: monthUtils.currentDay(),
    idioma: i18n.language?.startsWith('ca') ? 'ca' : 'es',
    nombre: nombre ?? '',
    trato: contexto ?? null,
    tono: avisos.tono,
  };

  function anadir(b: Omit<Burbuja, 'id'>) {
    const id = siguienteId.current++;
    setBurbujas(prev => [...prev, { ...b, id }]);
  }

  async function preguntar(textoPregunta: string) {
    const limpio = textoPregunta.trim().slice(0, LARGO_PREGUNTA);
    if (!limpio || pensando) {
      return;
    }
    setError(null);
    setPregunta('');
    anadir({ rol: 'user', texto: limpio, herramientas: [] });
    setPensando(true);
    try {
      const r = await preguntarAGavi(historial, limpio, {
        pedirTurno: mensajes =>
          pedirRumbo<RespuestaTurno>('/rumbo/gavi/turno', {
            metodo: 'POST',
            cuerpo: { mensajes, ficha },
          }),
        ejecutar: ejecutarHerramienta,
      });
      setHistorial(r.historial);
      if (r.gasto) {
        const gasto = r.gasto;
        setEstado(e => (e ? { ...e, gasto } : e));
      }
      anadir({
        rol: 'assistant',
        texto:
          r.texto ||
          (r.cortada
            ? t(
                'I got tangled up with that one. Ask me again in other words and we start fresh.',
              )
            : t('I have nothing to add.')),
        herramientas: r.herramientas,
        pantalla: r.pantalla,
      });
    } catch (e) {
      setError((e as Error).message);
      setPregunta(limpio);
      // La pregunta no llegó a contestarse: fuera de la lista.
      setBurbujas(prev => prev.slice(0, -1));
    }
    setPensando(false);
  }

  function nueva() {
    setBurbujas([]);
    setHistorial([]);
    setError(null);
  }

  const cabecera = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: espacio.fila,
        padding: `6px ${espacio.margen}px`,
        minHeight: 56,
      }}
    >
      <BotonRedondo
        icono="cl"
        aria-label={t('Back')}
        onPress={() => void navigate(-1)}
      />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          role="heading"
          aria-level={1}
          style={{ ...texto.titulo, color: color.fg, whiteSpace: 'nowrap' }}
        >
          <Trans>Ask Gavi</Trans>
        </Text>
        <Text style={{ fontSize: 13, fontWeight: 700, color: color.fg3 }}>
          <Trans>Questions about your money, in plain words</Trans>
        </Text>
      </View>
      {burbujas.length > 0 && (
        <Boton
          variante="fantasma"
          isDisabled={pensando}
          onPress={nueva}
          data-testid="gavi-nueva"
        >
          <Trans>New chat</Trans>
        </Boton>
      )}
    </View>
  );

  const contenido = !disponible ? (
    <Tarjeta>
      <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
        <Trans>
          Ask Gavi only works on your own budgets: one on this phone, or one in
          the cloud that you own.
        </Trans>
      </Text>
    </Tarjeta>
  ) : !estado ? (
    error ? (
      <AvisoError motivo={error} />
    ) : (
      <Cargando />
    )
  ) : (
    <>
      {burbujas.length === 0 && (
        <Bienvenida
          onPregunta={p => void preguntar(p)}
          desactivada={!estado.hayClave || pensando}
        />
      )}
      {burbujas.map(b => (
        <BurbujaGavi
          key={b.id}
          burbuja={b}
          onLlevar={p => void navigate(RUTAS_PANTALLA[p])}
        />
      ))}
      {pensando && (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Gavi estado="ladea" duracion={null} ancho={40} />
          <Text style={{ ...texto.secundario, color: color.fg3 }}>
            <Trans>Looking at your numbers…</Trans>
          </Text>
        </View>
      )}
      {!estado.hayClave && <AvisoError motivo="sin-clave" />}
      {error && <AvisoError motivo={error} />}
      <textarea
        data-testid="gavi-pregunta"
        aria-label={t('Ask Gavi')}
        value={pregunta}
        maxLength={LARGO_PREGUNTA}
        rows={2}
        placeholder={t('Ask something…')}
        disabled={!estado.hayClave}
        onChange={e => setPregunta(e.target.value)}
        style={estiloCampo}
      />
      <Boton
        bloque
        isDisabled={!pregunta.trim() || pensando || !estado.hayClave}
        onPress={() => void preguntar(pregunta)}
        data-testid="gavi-enviar"
      >
        <Trans>Ask</Trans>
      </Boton>
      <LineaGasto gasto={estado.gasto} />
      <div ref={final} />
    </>
  );

  return (
    <Page padding={0} header={cabecera}>
      <View
        data-testid="gavi"
        style={{
          gap: 12,
          padding: `${espacio.fila}px ${espacio.margen}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          flexShrink: 0,
        }}
      >
        {contenido}
      </View>
      {disponible && !aceptado && (
        <HojaPermiso
          onAceptar={() => {
            guardarPermiso();
            setAceptado(true);
          }}
          onRechazar={() => void navigate(-1)}
        />
      )}
    </Page>
  );
}

function Bienvenida({
  onPregunta,
  desactivada,
}: {
  onPregunta: (pregunta: string) => void;
  desactivada: boolean;
}) {
  const { t } = useTranslation();
  const ejemplos = [
    t('Where is my money going this month?'),
    t('How am I doing compared to last month?'),
    t('What if I save 100 € more each month?'),
  ];
  return (
    <View style={{ gap: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10 }}>
        <Gavi ancho={56} />
        <Text
          style={{
            ...texto.cuerpo,
            color: color.fg,
            padding: '9px 12px',
            borderRadius: 16,
            backgroundColor: color.surface2,
            flex: 1,
          }}
        >
          <Trans>
            Ask me about your money and I will look at your budget to answer. I
            only look: I never change anything.
          </Trans>
        </Text>
      </View>
      {ejemplos.map(e => (
        <Boton
          key={e}
          variante="fantasma"
          isDisabled={desactivada}
          onPress={() => onPregunta(e)}
          style={{ justifyContent: 'flex-start', textAlign: 'left' }}
        >
          {e}
        </Boton>
      ))}
    </View>
  );
}

function BurbujaGavi({
  burbuja,
  onLlevar,
}: {
  burbuja: Burbuja;
  onLlevar: (pantalla: Pantalla) => void;
}) {
  const { t } = useTranslation();
  const mia = burbuja.rol === 'user';
  const nombres: Record<string, string> = {
    resumen: t('the month summary'),
    mes_presupuesto: t('the plan'),
    gasto_agrupado: t('your spending'),
    buscar_movimientos: t('your transactions'),
    cuentas: t('your accounts'),
    que_pasa_si: t('the numbers'),
    calcular: t('the numbers'),
    patrimonio_neto: t('your net worth'),
    fin_deuda: t('your debts'),
    ritmo_patrimonio: t('how your net worth moves'),
  };
  // Por texto: calcular y que_pasa_si se llaman igual («las cuentas»).
  const miradas = [
    ...new Set(
      burbuja.herramientas.filter(h => h in nombres).map(h => nombres[h]),
    ),
  ].join(', ');
  const pantallas: Record<Pantalla, string> = {
    plan: t('Take me to the plan'),
    asignar: t('Take me to assign the month'),
    movimientos: t('Take me to transactions'),
    por_revisar: t('Take me to review'),
    cuentas: t('Take me to accounts'),
    informes: t('Take me to reports'),
    ahorros: t('Take me to savings goals'),
  };
  return (
    <View
      style={{
        alignSelf: mia ? 'flex-end' : 'flex-start',
        maxWidth: '88%',
        gap: 6,
      }}
    >
      {!mia && miradas && (
        <Text style={{ ...texto.secundario, color: color.fg3 }}>
          {t('Looked at: {{miradas}}', { miradas })}
        </Text>
      )}
      <Text
        style={{
          ...texto.cuerpo,
          whiteSpace: 'pre-wrap',
          padding: '9px 12px',
          borderRadius: 16,
          backgroundColor: mia ? color.accent : color.surface2,
          color: mia ? color.accentInk : color.fg,
        }}
      >
        {burbuja.texto}
      </Text>
      {burbuja.pantalla && (
        <Boton
          variante="fantasma"
          onPress={() => onLlevar(burbuja.pantalla as Pantalla)}
          data-testid="gavi-llevar"
          style={{ alignSelf: 'flex-start' }}
        >
          {pantallas[burbuja.pantalla]}
        </Boton>
      )}
    </View>
  );
}

function LineaGasto({ gasto }: { gasto: Gasto }) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: 4 }}>
      <Text
        style={{
          ...texto.secundario,
          color: color.fg3,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {t(
          'Spent this month: {{mes}} $ of {{topeMes}} $ · today {{dia}} $ of {{topeDia}} $',
          {
            mes: gasto.mes.toFixed(2),
            topeMes: gasto.topes.mes,
            dia: gasto.dia.toFixed(2),
            topeDia: gasto.topes.dia,
          },
        )}
      </Text>
      <BarraProgreso
        valor={gasto.topes.mes > 0 ? gasto.mes / gasto.topes.mes : 1}
        color={gasto.mes >= gasto.topes.mes * 0.8 ? color.warn : color.accent}
        aria-label={t('Spent this month')}
      />
    </View>
  );
}

function HojaPermiso({
  onAceptar,
  onRechazar,
}: {
  onAceptar: () => void;
  onRechazar: () => void;
}) {
  const { t } = useTranslation();
  const puntos = [
    t(
      'To answer you, the numbers I look at (amounts, categories, merchants, accounts and debts) travel to Claude, from Anthropic, in the United States.',
    ),
    t(
      'Rumbo does not keep the conversation. Anthropic may keep it up to 30 days for safety, and does not use it to train.',
    ),
    t('I only look: I never change anything in your budget.'),
    t(
      'It is a helping hand, not financial or tax advice. The decisions are yours.',
    ),
  ];
  return (
    <Hoja
      aria-label={t('Before we start')}
      data-testid="gavi-permiso"
      onCerrar={onRechazar}
    >
      <View style={{ gap: 12, padding: espacio.margen }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Gavi ancho={48} />
          <Text style={{ ...texto.titulo, fontSize: 22, color: color.fg }}>
            <Trans>Before we start</Trans>
          </Text>
        </View>
        {puntos.map(p => (
          <Text key={p} style={{ ...texto.cuerpo, color: color.fg2 }}>
            {`• ${p}`}
          </Text>
        ))}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Boton
            variante="fantasma"
            onPress={onRechazar}
            style={{ flex: 1 }}
            data-testid="gavi-permiso-no"
          >
            <Trans>Not now</Trans>
          </Boton>
          <Boton
            onPress={onAceptar}
            style={{ flex: 1 }}
            data-testid="gavi-permiso-si"
          >
            <Trans>OK, let's go</Trans>
          </Boton>
        </View>
      </View>
    </Hoja>
  );
}

function AvisoError({ motivo }: { motivo: string }) {
  const { t } = useTranslation();
  const textos: Record<string, string> = {
    forbidden: t('Only the person who runs Rumbo can see this.'),
    'sin-clave': t(
      'Gavi is asleep: the Claude API key is missing on the server.',
    ),
    'tope-mes': t(
      "Gavi has reached this month's spending limit. Back next month.",
    ),
    'tope-dia': t("Gavi has reached today's spending limit. Back tomorrow."),
    'sin-creditos': t(
      'The Claude API account has run out of credit. Top it up in the Claude Console.',
    ),
    'clave-mala': t(
      'The Claude API key on the server is not valid. Check it in the Claude Console.',
    ),
    saturada: t('Claude is very busy right now. Try again in a minute.'),
    'sin-servidor': t('The server could not be reached. Try again in a while.'),
  };
  return (
    <Text style={{ ...texto.secundario, color: color.bad }}>
      {textos[motivo] ?? t('Gavi could not answer. Try again in a while.')}
    </Text>
  );
}
